import { useTranslation } from 'react-i18next';
import useFormsIntegrationHelpers from '../../../hooks/useFormsIntegrationHelpers';
import { useNavigate, useParams } from 'react-router-dom';
import TableSkeleton from '../../../components/loaders/TableSkeleton';
import { useDispatch } from 'react-redux';
import { setBreadCrumbsData } from '../../../store/redux/breadCrumbsData';
import SingleOrderDetails from '../../../modules/orders/components/SingleOrderDetails';
import SingleOrderStep from '../../../modules/orders/components/SingleOrderStep';
import { useEffect, useState } from 'react';
import SectionHeader from '../../../components/SectionHeader';
import { IStepData } from '../../../modules/orders/types/interfaces';
import SingleOrderPayments from '../../../modules/orders/components/SingleOrderPayments';
import Button from '../../../components/buttons/Button';
import { axiosInstance } from '../../../config/axiosConfig';
import { navigateRoute } from '../../../utils/HelperFunctions';
import ModalContainer from '../../../components/ModalContainer';
import ConfirmModal from '../../../components/ConfirmModal';

const SingleOrder = () => {
  const { t } = useTranslation();
  const { id } = useParams();
  const [expanded, setExpanded] = useState(0);
  const navigate = useNavigate();
  const [cancelOrderOpened, setCancelOrderOpened] = useState(false);

  const { apiData, getDataLoading } = useFormsIntegrationHelpers({
    queryKey: ['orders', id],
    id,
    singleGetApi: `/admin/orders/${id}`,
    refetchOnWindowFocus: 'always'
  });
  const { apiData: stepsData, getDataLoading: stepsDataLoading } = useFormsIntegrationHelpers({
    queryKey: ['orders', 'steps', id],
    id,
    singleGetApi: `/admin/orders/${id}/steps?relations=responsibilities`,
    refetchOnWindowFocus: 'always'
  });

  const dispatch = useDispatch();
  dispatch(
    setBreadCrumbsData({
      page_title: t('order_details')
    })
  );

  const orderCancelled = apiData?.status === 'Cancelled';

  useEffect(() => {
    setExpanded(orderCancelled ? null : apiData?.current_step?.id || 0);
  }, [apiData]);

  async function handleCancelOrder() {
    await axiosInstance.post(`/admin/orders/${id}/cancel`);
    const contentContainer = document.querySelector('.layout_content');
    contentContainer?.scrollTo({ top: 0, behavior: 'smooth' });
  }

  if (getDataLoading || stepsDataLoading) return <TableSkeleton columns={6} withoutButton />;

  return (
    <div className="single-order-container">
      <SingleOrderDetails data={apiData} />
      <SingleOrderPayments data={apiData?.payments} />
      <SectionHeader title={t('steps')} customStyle="last">
        <div className="accordions-container" style={{ marginBottom: '2rem' }}>
          {stepsData?.map((step: IStepData, index: number) => (
            <SingleOrderStep
              data={step}
              index={index}
              currentStepOrder={apiData?.current_step?.order}
              orderCancelled={orderCancelled}
              expanded={expanded}
              setExpanded={setExpanded}
              orderedRefused={apiData?.customer_refused_vehicle}
            />
          ))}
        </div>
      </SectionHeader>
      {apiData?.status !== 'Cancelled' && apiData?.status !== 'Paid And Finished' && !apiData?.customer_refused_vehicle && (
        <div className={`form_button reverse`} style={{ display: 'flex', justifyContent: 'flex-end', gap: '1em' }}>
          <Button
            text={t('edit_order')}
            onClick={(e) => {
              navigateRoute(e, `/orders/create-order/${id}`, navigate);
            }}
            type={'submit'}
          />
          <Button
            text={t('cancel_order')}
            customClass="red"
            onClick={() => {
              setCancelOrderOpened(true);
            }}
            type={'submit'}
          />
        </div>
      )}
      {cancelOrderOpened && (
        <ModalContainer small>
          <ConfirmModal
            closeModal={() => setCancelOrderOpened(false)}
            queryKey={'orders'}
            message={t('are_you_sure_you_want_to_cancel_this_order')}
            confirmFunc={async (handleSuccess) => {
              await handleCancelOrder();
              handleSuccess();
            }}
            successMsg={t('order_cancelled')}
          />
        </ModalContainer>
      )}
    </div>
  );
};

export default SingleOrder;
